import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiRequest, ApiError } from "../api/apiClient";

const AuthContext = createContext(null);

function normalizeUser(payload) {
  return payload?.user || payload?.session?.user || null;
}

export function getRoleHome(role) {
  const normalizedRole = String(role || "").toLowerCase();
  if (["seller", "buyer", "advisor", "partner"].includes(normalizedRole)) {
    return `/${normalizedRole}`;
  }
  return "/";
}

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [sessionError, setSessionError] = useState("");
  const applyAuthResult = useCallback((payload) => {
    const nextUser = normalizeUser(payload);
    if (nextUser) {
      setUser(nextUser);
      setStatus("authenticated");
      setSessionError("");
    }
    return payload;
  }, []);
  const refreshSession = useCallback(async () => {
    setStatus("loading");
    setSessionError("");
    try {
      const payload = await apiRequest("/api/auth/session");
      const nextUser = normalizeUser(payload);
      if (nextUser) {
        setUser(nextUser);
        setStatus("authenticated");
      } else {
        setUser(null);
        setStatus("anonymous");
      }
      return payload;
    } catch (error) {
      setUser(null);
      setStatus("anonymous");
      if (!(error instanceof ApiError && error.status === 401)) {
        setSessionError(error.message || "Unable to restore the current session.");
      }
      return null;
    }
  }, []);
  useEffect(() => {
    refreshSession();
  }, [refreshSession]);
  const login = useCallback(
    async (credentials) =>
      applyAuthResult(await apiRequest("/api/auth/login", { method: "POST", body: credentials })),
    [applyAuthResult]
  );
  const register = useCallback(
    async (details) => apiRequest("/api/auth/register", { method: "POST", body: details }),
    []
  );
  const verifyEmail = useCallback(
    async (details) =>
      applyAuthResult(await apiRequest("/api/auth/verify-email", { method: "POST", body: details })),
    [applyAuthResult]
  );
  const resendVerification = useCallback(
    async (email) => apiRequest("/api/auth/resend-verification", { method: "POST", body: { email } }),
    []
  );
  const verifyPasswordReset = useCallback(
    async (details) => apiRequest("/api/auth/verify-password-reset", { method: "POST", body: details }),
    []
  );
  const forgotPassword = useCallback(
    async (email) => apiRequest("/api/auth/forgot-password", { method: "POST", body: { email } }),
    []
  );
  const resetPassword = useCallback(
    async (details) => apiRequest("/api/auth/reset-password", { method: "POST", body: details }),
    []
  );
  const setupMfa = useCallback(
    async () => apiRequest("/api/auth/mfa/setup", { method: "POST" }),
    []
  );
  const verifyMfa = useCallback(
    async (details) =>
      applyAuthResult(await apiRequest("/api/auth/mfa/verify", { method: "POST", body: details })),
    [applyAuthResult]
  );
  const logout = useCallback(async () => {
    try {
      await apiRequest("/api/auth/logout", { method: "POST" });
    } finally {
      setUser(null);
      setStatus("anonymous");
      setSessionError("");
    }
  }, []);
  const value = useMemo(
    () => ({
      status,
      user,
      sessionError,
      isAuthenticated: status === "authenticated",
      refreshSession,
      login,
      register,
      verifyEmail,
      resendVerification,
      verifyPasswordReset,
      forgotPassword,
      resetPassword,
      setupMfa,
      verifyMfa,
      logout,
    }),
    [
      status,
      user,
      sessionError,
      refreshSession,
      login,
      register,
      verifyEmail,
      resendVerification,
      verifyPasswordReset,
      forgotPassword,
      resetPassword,
      setupMfa,
      verifyMfa,
      logout,
    ]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }
  return context;
}
